<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Add to Group</div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="props.user.id" :closeable="false" shadow>
                Selected user <strong class="font-bold">{{ props.user.email }}</strong>
            </vx-alert>

            <team-user-group-assignment v-model="assignedGroups" ref="groupAssignmentRef" />
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="updateVisible(false)"
                    type="button"
                    data-test="cancel-button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit"
                    data-test="add-button"
                    :loading="submitting"
                    :disabled="assignedGroups.length === 0"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Add <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxModal } from '@voxie/frontend-components';
import { groupUsersClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import TeamUserGroupAssignment from './TeamUserGroupAssignment.vue';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';
import { HTTP_CREATED } from '~/support/HttpStatuses';

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    user: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['updatedGroupUser']);

const toasted = useToasted();
const submitting = ref(false);

const groupAssignmentRef = ref(null);
const assignedGroups = ref([]);

const updateVisible = (isVisible) => {
    groupAssignmentRef.value?.reset();
    visible.value = isVisible;
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const groupInvitePromises = assignedGroups.value.map(async (group) => {
            try {
                return groupUsersClient.upsertGroupUser(props.teamId, group.group_id, props.user.id, {
                    role: group.role,
                });
            } catch (groupError) {
                const message = `Failed to invite user to group ${group.name}:`;
                toasted.error(`${message}: ${catchRequestError(groupError)}`);
            }
        });

        const responses = await Promise.allSettled(groupInvitePromises);

        const groupsAdded = responses.reduce((acc, currentResponse) => {
            if (currentResponse?.value?.status === HTTP_CREATED) {
                acc++;
            }

            return acc;
        }, 0);

        emit('updatedGroupUser', {
            user_id: props.user.id,
            groups_added: groupsAdded,
        });
        toasted.success('User updated successfully');

        groupAssignmentRef.value?.reset();

        updateVisible(false);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
